import * as React from "react";
import { useEffect, useState } from 'react';
import { CallClient, VideoDeviceInfo, AudioDeviceInfo, DeviceManager } from '@azure/communication-calling';
import { Dropdown, DropdownProps, Text, Flex, MicOffIcon, CallVideoOffIcon } from '@fluentui/react-northstar';
import './custom-call-screen.scss';

interface DeviceSelectorProps {
    callClient: CallClient;
    selectedAudioId: string | null;
    selectedVideoId: string | null;
    onDevicesSelected: (audioDeviceId: string, videoDeviceId: string) => void;
    deviceError: string | undefined;
}

const DeviceSelector: React.FC<DeviceSelectorProps> = ({
    callClient,
    selectedAudioId,
    selectedVideoId,
    onDevicesSelected,
    deviceError,
}) => {
    const [deviceManager, setDeviceManager] = useState<DeviceManager | null>(null);
    const [audioDevices, setAudioDevices] = useState<AudioDeviceInfo[]>([]);
    const [videoDevices, setVideoDevices] = useState<VideoDeviceInfo[]>([]);
    const [localAudioId, setLocalAudioId] = useState<string | null>(null);
    const [localVideoId, setLocalVideoId] = useState<string | null>(null);

    useEffect(() => {
        const initDevices = async () => {
            const manager = await callClient.getDeviceManager();
            await manager.askDevicePermission({ audio: true, video: true });

            const audios = await manager.getMicrophones();
            const videos = await manager.getCameras();

            setDeviceManager(manager);
            setAudioDevices(audios);
            setVideoDevices(videos);

            const defaultAudioId = selectedAudioId || audios[0]?.id || '';
            const defaultVideoId = selectedVideoId || videos[0]?.id || '';

            setLocalAudioId(defaultAudioId);
            setLocalVideoId(defaultVideoId);

            onDevicesSelected(defaultAudioId, defaultVideoId);
        };

        if (!deviceError)
            initDevices();

        return () => {

            setDeviceManager(null);
        };
    }, [callClient]);

    useEffect(() => {
        if (selectedAudioId) setLocalAudioId(selectedAudioId);
        if (selectedVideoId) setLocalVideoId(selectedVideoId);
    }, [selectedAudioId, selectedVideoId]);

    const handleAudioChange = (_: any, data: DropdownProps) => {
        const item = data.value as { key: string, header: string };
        setLocalAudioId(item.key);
        if (item.key && localVideoId) {
            onDevicesSelected(item.key, localVideoId);
        }
    };

    const handleVideoChange = (_: any, data: DropdownProps) => {
        const item = data.value as { key: string, header: string };
        setLocalVideoId(item.key);
        if (localAudioId && item.key) {
            onDevicesSelected(localAudioId, item.key);
        }
    };

    return (
        <Flex gap="gap.medium" column>
            <Text content="Microfone" />
            {!deviceError ?
                <Dropdown
                    items={audioDevices.map(dev => ({ key: dev.id, header: dev.name }))}
                    onChange={handleAudioChange}
                    value={audioDevices.length && { key: localAudioId, header: audioDevices.find(d => d.id === localAudioId)?.name || '' }}
                    placeholder={!audioDevices.length ? "Carregando..." : "Selecione o microfone"}
                    fluid
                    checkable
                />
                :
                <Flex className="device-error-label" gap="gap.small" vAlign="center">
                    <MicOffIcon outline />
                    <Text content="Microfone bloqueado" />
                </Flex>
            }
            

            <Text content="Câmera" />

            {!deviceError ?
                <Dropdown
                    items={videoDevices.map(dev => ({ key: dev.id, header: dev.name }))}
                    onChange={handleVideoChange}
                    value={videoDevices.length && { key: localVideoId, header: videoDevices.find(d => d.id === localVideoId)?.name || '' }}
                    placeholder={!videoDevices.length ? "Carregando..." : "Selecione a câmera"}
                    fluid
                    checkable
                />
                :
                <Flex className="device-error-label" gap="gap.small" vAlign="center">
                    <CallVideoOffIcon outline />
                    <Text content="Câmera bloqueada" />
                </Flex>
            }
            
        </Flex>
    );
};

export default DeviceSelector;
